import { GlobalMessages } from '@shared/localization/messages';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { Controller, useController } from 'react-hook-form';
import type { ClassNamesConfig, GroupBase, Props } from 'react-select';
import Select from 'react-select';

import { useMessages } from '@/hooks/messages';

import FieldErrors from './FieldErrors';

type OptionType = {
  value: string;
  label: string;
};

export type SelectFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  placeholder?: string;
  onChange?: (value: OptionType | null) => void;
  value?: OptionType | null;
  menuListClassName?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  classNames?: ClassNamesConfig<OptionType, false>;
} & Props<OptionType, false, GroupBase<OptionType>>;

export const SelectFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  fieldPath,
  control,
  rules,
  options,
  isDisabled,
  placeholder,
  onChange,
  value: _value,
  isClearable,
  classNames,
}: SelectFormFieldProps<TFieldValues, TFieldPath>) => {
  const globalMessages = useMessages(GlobalMessages);

  const {
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  return (
    <div>
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: { onChange: onFormChange, value } }) => (
          <Select
            value={
              options?.find((c) => value === (c as OptionType).value) as OptionType | undefined
            }
            onChange={(val) => {
              onFormChange((val as OptionType)?.value);
              if (onChange) {
                onChange(val as OptionType | null);
              }
            }}
            classNames={classNames}
            options={options}
            isDisabled={isDisabled}
            isClearable={isClearable}
            classNamePrefix='custom-select'
            placeholder={placeholder || globalMessages.select()}
            // Portal to <body> so the menu escapes a modal's overflow/transform
            // and anchors under the field; raise z-index above the modal stack.
            menuPosition='fixed'
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            styles={{
              menu: (provided) => ({
                ...provided,
                width: 'max-content',
                minWidth: '100%',
                zIndex: 1000,
              }),
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 20000,
              }),
            }}
          />
        )}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
